import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { bapi } from '@/lib/billingApi';

export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Link copied'); } catch { toast.error('Copy failed, select the link and copy it'); }
};

export const AdLinkDialog = ({ open, onClose, destinations, onCreated }) => {
  const [form, setForm] = useState({ name: '', campaign: '', destination: '/' });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  useEffect(() => { if (open) { setForm({ name: '', campaign: '', destination: '/' }); setCreated(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const link = await bapi('/admin/growth/ad-links', { method: 'POST', body: form });
      setCreated(link);
      onCreated(link);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="ad-link-dialog">
        <DialogHeader>
          <DialogTitle>{created ? 'Your ad link is ready' : 'New Facebook ad link'}</DialogTitle>
          <DialogDescription>
            {created ? 'Paste this as the Website URL in Facebook Ads Manager.' : 'Make one link per ad so every visitor and signup shows which ad brought them.'}
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm break-all font-mono text-slate-700" data-testid="ad-link-created-url">{created.url}</div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} data-testid="ad-link-done-btn">Done</Button>
              <Button onClick={() => copyText(created.url)} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="ad-link-copy-new-btn">
                <Copy className="w-4 h-4 mr-2" />Copy link
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ad-name">Ad name</Label>
              <Input id="ad-name" value={form.name} onChange={set('name')} placeholder="e.g. Promo video 55s" maxLength={80} required data-testid="ad-link-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-campaign">Campaign</Label>
              <Input id="ad-campaign" value={form.campaign} onChange={set('campaign')} placeholder="e.g. Spring signups" maxLength={80} required data-testid="ad-link-campaign-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Page the ad opens</Label>
              <Select value={form.destination} onValueChange={set('destination')}>
                <SelectTrigger data-testid="ad-link-destination-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {destinations.map((d) => <SelectItem key={d.path} value={d.path} data-testid={`ad-link-dest-${d.path.replace(/\W+/g, '') || 'home'}`}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={busy} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="ad-link-create-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Create link
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
